/*
 *  ************************************************************************
 *  *  © [2015 - 2024] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import chevron from '../../../../../../assets/icons/down-arrow.svg';
import { SvgHandler } from '../../../../svg-handler/svg-handler';
import { Headline } from '../../atoms/headline';
import { Button } from '../../atoms/button';
import { SocialLogin } from '../social-login';
import {
  EMAIL_LABEL,
  QUILL_EMPTY_STRING,
  SIGN_IN_HEADLINE,
  UPDATE_SIGN_IN,
  UPDATE_SINGLE_SIGN_ON,
  ENTER_EMAIL_PHONE_NUMBER,
  DEFAULT_COUNTRY_CODE,
  OR_LABEL,
  CONTINUE_BUTTON
} from '../../../../../constants';
import {
  emailValidation,
  triggerSendOtp,
  triggerEmailCheck,
  getErrorStyle,
  genericError,
  inputFieldError,
  handleScrollToTop
} from '../../util';
import { ErrorMessage } from '../../atoms/error-message';
import get from 'lodash/get';
import './signin.module.css';
import { getPath } from '../../../../../utils';

let countryList = [];
let importedModule = null;

const Description = ({ pageDescription }) => {
  const publisherSettingsDescription = useSelector(state =>
    get(state, ['qt', 'config', 'publisher-settings', 'description'], '')
  );
  const description =
    pageDescription && pageDescription !== QUILL_EMPTY_STRING ? pageDescription : publisherSettingsDescription;

  if (description) {
    return <div styleName='description' dangerouslySetInnerHTML={{ __html: description }} />;
  }
  return null;
};

export const SignInScreen = ({ mode, isUserLoginPage = false }) => {
  const { singleSignOn = {}, signIn = {} } = useSelector(state => get(state, ['login'], {}));
  const { inputData = '', countryCode = '', countryIso = '' } = get(signIn, ['userData'], {});
  const {
    inputData: ssoInputData = '',
    countryCode: ssoCountryCode = '',
    countryIso: ssoCountryIso = ''
  } = get(singleSignOn, ['userData'], {});

  const loginSetting = useSelector(state => get(state, ['qt', 'config', 'pagebuilder-config', 'login'], {}));
  const sketchesHost = useSelector(state => get(state, ['qt', 'config', 'sketches-host'], ''));
  const whiteListedUrlParams = useSelector(state =>
    get(state, ['qt', 'config', 'pagebuilder-config', 'general', 'others', 'whiteListedUrlParams'], [])
  );
  const {
    mobileOtpAuth = false,
    defaultCountryCode = '+93',
    defaultCountryIso = 'af',
    googleAuth = true,
    facebookAuth = true,
    linkedInAuth = true,
    appleAuth = false
  } = get(loginSetting, ['pageSetting'], {});
  const { button, emailLabel, phoneEmailLabel, orLabel, pageDescription, pageTitle, additionalInfo } = get(
    loginSetting,
    ['sign-in', 'rows', 0, 'config'],
    {}
  );
  const localStorageEmail = global?.localStorage?.getItem('userEmail');
  const userInputData = inputData || localStorageEmail;
  const storedUserInfo = !isUserLoginPage ? userInputData : ssoInputData;
  const [displayDropdown, setDropdown] = useState(false);
  const countryCodeData = !isUserLoginPage ? countryCode : ssoCountryCode;
  const [countryCodeValue, setCountryCodeValue] = useState(countryCodeData || defaultCountryCode);
  const [userInfo, setUserInfo] = useState(storedUserInfo);
  const [isEmailValid, setEmailValidation] = useState(emailValidation(storedUserInfo));
  const [response, setResponse] = useState(null);
  const [api, setApi] = useState(null);
  const countryIsoData = !isUserLoginPage ? countryIso : ssoCountryIso;
  const [countryIsoValue, setCountryIsoValue] = useState(countryIsoData || defaultCountryIso);
  const dispatch = useDispatch();
  const title = pageTitle && pageTitle !== QUILL_EMPTY_STRING ? pageTitle : SIGN_IN_HEADLINE;

  handleScrollToTop();

  useEffect(() => {
    if (mobileOtpAuth) {
      if (importedModule === null) {
        importedModule = import(/* webpackChunkName: "country-telephone-data" */ 'country-telephone-data')
          .then(({ allCountries }) => {
            countryList = allCountries;
          })
          .catch(err => {
            console.log('Failed to load package for country telephone code', err);
            return Promise.reject();
          });
      }
    }
    return;
  }, [mobileOtpAuth]);

  const triggerHandler = async () => {
    const updatedUserInfo = userInfo.trim();
    if (isEmailValid) {
      const emailCheckResponse = await triggerEmailCheck({ email: updatedUserInfo, sketchesHost });

      if (emailCheckResponse) {
        global?.localStorage?.setItem('userEmail', updatedUserInfo);
        dispatch({
          type: !isUserLoginPage ? UPDATE_SIGN_IN : UPDATE_SINGLE_SIGN_ON,
          payload: { currentScreen: 'password', userData: { inputData: updatedUserInfo } }
        });
      } else {
        if (!isUserLoginPage) {
          global?.localStorage?.setItem('userEmail', updatedUserInfo);
          window.location.href = getPath({
            sketchesHost,
            relativePath: '/sign-up',
            injectCustomParams: true,
            whiteListedUrlParams
          });
        } else {
          dispatch({
            type: UPDATE_SINGLE_SIGN_ON,
            payload: { currentScreen: 'signUp', userData: { inputData: updatedUserInfo } }
          });
        }
      }
    } else if (mobileOtpAuth) {
      const phoneNumber = `${countryCodeValue}${updatedUserInfo}`;
      const sendOtpProps = { phoneNumber: phoneNumber, countryIso: countryIsoValue, sketchesHost };
      const otpResponse = await triggerSendOtp(sendOtpProps);

      setResponse(otpResponse);
      setApi('send-otp-mobile');
      if (otpResponse === 200) {
        dispatch({
          type: !isUserLoginPage ? UPDATE_SIGN_IN : UPDATE_SINGLE_SIGN_ON,
          payload: {
            currentScreen: !isUserLoginPage ? 'otp' : 'mobileOtp',
            userData: {
              inputData: updatedUserInfo || inputData,
              countryCode: countryCodeValue,
              countryIso: countryIsoValue
            }
          }
        });
      }
    }
  };
  const displayCountryCode = mobileOtpAuth && !isEmailValid && userInfo;
  const getInputField = () => {
    let label;
    let userId;

    if (mobileOtpAuth) {
      label = phoneEmailLabel || ENTER_EMAIL_PHONE_NUMBER;
      userId = 'user-info';
    } else {
      label = emailLabel || EMAIL_LABEL;
      userId = 'user-email';
    }

    return (
      <>
        <label for={userId}>{label}</label>
        <div styleName={displayCountryCode ? 'overlay-wrapper' : ''}>
          <input
            type='text'
            value={userInfo}
            id={userId}
            onChange={event => {
              setResponse(null);
              setApi(null);
              setUserInfo(event.target.value);
              const isEmailValid = emailValidation(event.target.value);
              setEmailValidation(isEmailValid);
            }}
            name={userId}
            required
            styleName={displayCountryCode ? 'country-code-space' : ''}
          />
          {displayCountryCode && getCountryCodeDropdown()}
        </div>
        {inputFieldError(response, api) ? <ErrorMessage response={response} api={api} mode={mode} /> : <></>}
      </>
    );
  };

  const getCountryCodeDropdown = () => {
    return (
      <>
        <div
          styleName='overlay'
          data-test-id='selected-plan'
          onClick={() => {
            setResponse(null);
            setApi(null);
            setDropdown(!displayDropdown);
          }}
        >
          {countryCodeValue || DEFAULT_COUNTRY_CODE}
          <span styleName='chevron-wrapper'>
            <SvgHandler xlinkHref={chevron} width='24' height='24' />
          </span>
        </div>
        {displayDropdown && countryList.length ? (
          <div styleName='dropdown-container' data-test-id='dropdown'>
            {countryList.map((country, index) => (
              <div
                styleName='dropdown'
                key={index}
                data-test-id={`dropdown-${index}`}
                onClick={e => {
                  setCountryCodeValue(`+${country?.dialCode}`);
                  setCountryIsoValue(country?.iso2);
                  setDropdown(false);
                }}
              >
                <span styleName='country-name'>{country?.name}</span>
                <span styleName='country-code'>{`+${country?.dialCode}`}</span>
              </div>
            ))}
          </div>
        ) : null}
      </>
    );
  };

  const disableButton = (!mobileOtpAuth && (!userInfo || !isEmailValid)) || !userInfo;

  return (
    <>
      <div className={`auth--wrapper ${mode}`} styleName={mode}>
        <Headline content={title} />
        <Description pageDescription={pageDescription} />
        <div className={getErrorStyle(response, api, mode)} styleName='input-wrapper'>
          {getInputField()}
        </div>
        <Button
          text={button || CONTINUE_BUTTON}
          clickHandler={() => triggerHandler()}
          disable={disableButton}
          enableEnterClick={true}
        />
        {genericError(response, api) ? <ErrorMessage response={response} api={api} mode={mode} /> : <></>}
        {googleAuth || facebookAuth || linkedInAuth || appleAuth ? (
          <>
            <div styleName='continue-wrapper'>
              <div>{orLabel || OR_LABEL}</div>
            </div>
            <SocialLogin mode={mode} />
          </>
        ) : (
          <></>
        )}
      </div>
      {additionalInfo && (
        <div styleName='additionalInfo'>
          <Description pageDescription={additionalInfo} />
        </div>
      )}
    </>
  );
};
